import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {clearSelection} from 'src/components/data-table-selection';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {UnassignAccountEntitlementsTool} from 'src/modules/bulk-update';
import {EntitlementsPagination, NoResultPage, PreparedEntitlementsView, ResourceSuggest, useExpandRoles} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';


import {
    accountsEntitlementsControlHeader,
    accountsEntitlementsLoadingContainer,
    accountsEntitlementsTools,
} from '../account-entitlements.module.styl';

import {ResourceNameHeader} from './components/respource-name.header';
import {
    NO_ASSIGNED_ENTITLEMENT_DESCRIPTION,
    NO_ASSIGNED_ENTITLEMENT_TITLE,
    SECTION_NAME,
    SELECTION_KEY,
} from './constants';
import {useGetAccountEntitlements} from './hooks/useGetAccountEntitlements';
import {accountEntitlementsStateActions, accountEntitlementsStateSelector} from './slices/account-entitlements-state.slice';
import {accountEntitlementsActions} from './slices/account-entitlements.slice';


type Props = {
    accountId: number,
};

export const EntitlementsTab = ({accountId}: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {canEditAccountEntitlements} = useAdminAccessRight();
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {loadingError, result, cacheKeys} = useGetAccountEntitlements({accountId});
    const {
        disabled: isRolesDisabled,
        isExpanded: isRolesExpanded,
        toggleRolesExpansion,
        expandedRoleIds,
        setExpandedRoleIds,
    } = useExpandRoles({records: result?.data || []});

    const redirectToAssignPage = useCallback(() => {
        dispatch(accountEntitlementsStateActions.setFilters({}));
        navigate(`/profile/account/${accountId}/assign-resources/`);
    }, [accountId, navigate, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(accountEntitlementsActions.clearData());
            dispatch(accountEntitlementsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshResults = useCallback(() => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(accountEntitlementsActions.requireRefresh());
    }, [dispatch]);

    const filterResources = useCallback((resourceIds: string[] | null, query: string) => {
        dispatch(accountEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery: query,
        } : {}));
    }, [dispatch]);

    const noAccountResultMessage = useMemo(() => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <NoResultPage
                    title="No resources found for the selected criteria."
                    description=""
                />
            );
        }

        if (result && !result.data.length) {
            return (
                <NoResultPage
                    title={NO_ASSIGNED_ENTITLEMENT_TITLE}
                    description={NO_ASSIGNED_ENTITLEMENT_DESCRIPTION}
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    return (
        <>
            <ServerResponseHandler serverError={loadingError} sectionName={SECTION_NAME}>
                <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                    <StickyElement>
                        <div className={accountsEntitlementsControlHeader}>
                            <Group justify="space-between">
                                <ResourceSuggest
                                    onSearch={filterResources}
                                    accountId={accountId}
                                    id="resource-suggest"
                                />
                                <Group>
                                    <Group alignItems="center">
                                        <Label>Expand Roles</Label>
                                        <StateButton
                                            {...{
                                                name: 'expand-roles',
                                                disabled: isRolesDisabled,
                                                checked: isRolesExpanded,
                                                onChange: toggleRolesExpansion,
                                            }}
                                        />
                                    </Group>
                                    {canEditAccountEntitlements(accountId) && (
                                        <Button variant="Type.PRIMARY" onClick={redirectToAssignPage}>
                                            Assign Resource(s)
                                        </Button>
                                    )}
                                </Group>
                            </Group>
                        </div>
                    </StickyElement>
                    <BoxLoading loading={!result}>
                        <div className={accountsEntitlementsLoadingContainer}>
                            {result?.data.length ? (
                                <PreparedEntitlementsView
                                    resourceFilter={(cacheKeys.filters.resourceQuery as string | undefined) || ''}
                                    dataTableId="assign-account-entitlements"
                                    rows={result.data}
                                    selectionKey={SELECTION_KEY}
                                    toolRibbonClassName={accountsEntitlementsTools}
                                    customOffset={137}
                                    expandedRoleIds={expandedRoleIds}
                                    setExpandedRoleIds={setExpandedRoleIds}
                                    readonly={!canEditAccountEntitlements(accountId)}
                                    getColumnWidthByColumnId={getColumnWidthByColumnId}
                                    onStopResize={onStopResize}
                                    paginationStripe={(
                                        <EntitlementsPagination
                                            totalCount={result.totalCount}
                                            stateActions={accountEntitlementsStateActions}
                                            stateSelector={accountEntitlementsStateSelector}
                                        />
                                    )}
                                    shortHeader={<ResourceNameHeader/>}>
                                    {() => (
                                        <UnassignAccountEntitlementsTool
                                            accountId={accountId}
                                            selectionKey={SELECTION_KEY}
                                            onDone={refreshResults}
                                        />
                                    )}
                                </PreparedEntitlementsView>
                            ) : noAccountResultMessage}
                        </div>
                    </BoxLoading>
                </ErrorBoundary>
            </ServerResponseHandler>
        </>
    );
};
